import { useState, useEffect } from 'react';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, FileText, Download,
  BarChart3, FileSpreadsheet, Eye, Receipt, Building2,
  ChevronRight, ChevronDown, Wallet, CreditCard, Users,
  Landmark, Edit2, ExternalLink, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function AccountingReports({ profitLoss, taxSummary, fiscalYear, shareholderAllocations, setActiveTab }) {
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [reportPreviewData, setReportPreviewData] = useState(null);
  const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState('income-statement');
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [showAccountDrilldown, setShowAccountDrilldown] = useState(false);
  const [drilldownAccount, setDrilldownAccount] = useState(null);
  const [drilldownTransactions, setDrilldownTransactions] = useState([]);

  // Comparative P&L state
  const [comparative, setComparative] = useState(null);
  const [comparativeLoading, setComparativeLoading] = useState(false);
  const [compYear1, setCompYear1] = useState(fiscalYear - 1);
  const [compYear2, setCompYear2] = useState(fiscalYear);

  useEffect(() => {
    setCompYear2(fiscalYear);
    setCompYear1(fiscalYear - 1);
  }, [fiscalYear]);

  const loadComparative = async () => {
    setComparativeLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/api/accounting/reports/profit-loss/comparative?year1=${compYear1}&year2=${compYear2}`, { headers: { Authorization: `Bearer ${token}` } });
      setComparative(res.data);
    } catch (err) {
      toast.error('Failed to load comparative P&L');
    } finally {
      setComparativeLoading(false);
    }
  };

  useEffect(() => {
    if (compYear1 && compYear2 && compYear1 !== compYear2) loadComparative();
  }, [compYear1, compYear2]);

  const downloadPdfReport = async (reportId, filename) => {
    const token = getToken();
    try {
      const url = reportId.includes('?')
        ? `${API}/api/accounting/reports/pdf/${reportId}`
        : `${API}/api/accounting/reports/pdf/${reportId}?fiscal_year=${fiscalYear}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const urlObj = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlObj;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(urlObj);
      toast.success(`Downloaded: ${filename}`);
    } catch (err) {
      toast.error(`Failed to download ${filename}`);
    }
  };

  const loadReportPreview = async (reportType) => {
    const token = getToken();
    setReportPreviewLoading(true);
    setSelectedReportType(reportType);
    setExpandedAccounts({});
    try {
      const response = await axios.get(`${API}/api/accounting/reports/preview?report_type=${reportType}&fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } });
      setReportPreviewData(response.data);
      setShowReportPreview(true);
    } catch (err) {
      toast.error('Failed to load report preview');
    } finally {
      setReportPreviewLoading(false);
    }
  };

  const toggleAccountExpand = (accountId) => {
    setExpandedAccounts(prev => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  const loadAccountDrilldown = async (account) => {
    const token = getToken();
    setDrilldownAccount(account);
    try {
      const response = await axios.get(`${API}/api/accounting/reports/account-transactions/${account.account_id}?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } });
      setDrilldownTransactions(response.data.transactions);
      setShowAccountDrilldown(true);
    } catch (err) {
      toast.error('Failed to load account transactions');
    }
  };

  const navigateToTransactionById = (transactionId) => {
    setShowAccountDrilldown(false);
    setShowReportPreview(false);
    setActiveTab('transactions');
    setTimeout(() => {
      const element = document.querySelector(`[data-transaction-id="${transactionId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('bg-yellow-100');
        setTimeout(() => element.classList.remove('bg-yellow-100'), 3000);
      }
    }, 500);
  };

  const navigateToInvoiceById = () => {
    setShowAccountDrilldown(false);
    setShowReportPreview(false);
    setActiveTab('invoices');
  };

  const renderAccountSection = (accounts, title, Icon, colorClass, valueKey) => {
    if (!accounts || accounts.length === 0) return null;
    return (
      <div>
        <h4 className={`font-semibold ${colorClass} mb-2 flex items-center gap-2`}>
          <Icon className="w-4 h-4" /> {title}
        </h4>
        <div className="space-y-2">
          {accounts.map(account => (
            <div key={account.account_id} className="border rounded-lg">
              <div className="p-3 flex justify-between items-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => toggleAccountExpand(account.account_id)}>
                <div className="flex items-center gap-3">
                  {expandedAccounts[account.account_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <span className="font-mono text-sm text-slate-500">{account.account_number}</span>
                  <span className="font-medium">{account.account_name}</span>
                  <Badge variant="outline" className="text-xs">{account.transaction_count} trans</Badge>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`font-mono font-bold ${colorClass}`}>{formatCurrency(account[valueKey] || account.net_balance)}</span>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); loadAccountDrilldown(account); }}>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {expandedAccounts[account.account_id] && account.transactions && (
                <div className="border-t bg-slate-50 p-3">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Description</TableHead>
                        <TableHead className="text-xs text-right">Amount</TableHead>
                        <TableHead className="text-xs text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {account.transactions.slice(0, 10).map(trans => (
                        <TableRow key={trans.id} className="text-xs">
                          <TableCell>{trans.date}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{trans.description}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(trans.debit || trans.credit)}</TableCell>
                          <TableCell className="text-center">
                            <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => navigateToTransactionById(trans.id)}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {account.transactions.length > 10 && (
                    <p className="text-xs text-slate-500 mt-2">... and {account.transactions.length - 10} more transactions</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Profit & Loss */}
      {profitLoss && (
        <Card data-testid="profit-loss-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Profit & Loss Statement</CardTitle>
            <CardDescription>Period: {profitLoss.period.start} to {profitLoss.period.end}</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Revenue */}
            <div className="mb-6">
              <h4 className="font-semibold text-emerald-700 mb-3">Revenue</h4>
              {profitLoss.income.map((item, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b"><span className="pl-4">{item.category}</span><span className="font-mono">{formatCurrency(item.amount)}</span></div>
              ))}
              <div className="flex justify-between py-2 font-bold text-emerald-700 bg-emerald-50 px-2 rounded mt-1" data-testid="total-income"><span>Total Revenue</span><span className="font-mono">{formatCurrency(profitLoss.total_income)}</span></div>
            </div>

            {/* Cost of Sales */}
            <div className="mb-6">
              <h4 className="font-semibold text-orange-700 mb-3">Cost of Sales</h4>
              {(profitLoss.cost_of_sales || []).map((item, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b"><span className="pl-4">{item.category}</span><span className="font-mono">{formatCurrency(item.amount)}</span></div>
              ))}
              <div className="flex justify-between py-2 font-bold text-orange-700 bg-orange-50 px-2 rounded mt-1" data-testid="total-cost-of-sales"><span>Total Cost of Sales</span><span className="font-mono">{formatCurrency(profitLoss.total_cost_of_sales)}</span></div>
            </div>

            {/* Gross Profit */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center" data-testid="gross-profit">
              <span className="text-lg font-semibold text-blue-800">Gross Profit</span>
              <span className={`text-xl font-bold font-mono ${profitLoss.gross_profit >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                {formatCurrency(profitLoss.gross_profit)}
              </span>
            </div>

            {/* Operating Expenses */}
            <div className="mb-6">
              <h4 className="font-semibold text-red-700 mb-3">Operating Expenses</h4>
              {(profitLoss.operating_expenses || []).map((item, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b"><span className="pl-4">{item.category}</span><span className="font-mono">{formatCurrency(item.amount)}</span></div>
              ))}
              <div className="flex justify-between py-2 font-bold text-red-700 bg-red-50 px-2 rounded mt-1" data-testid="total-operating-expenses"><span>Total Operating Expenses</span><span className="font-mono">{formatCurrency(profitLoss.total_operating_expenses)}</span></div>
            </div>

            {/* Net Income Before Tax */}
            <div className="p-4 bg-slate-100 rounded-lg flex justify-between items-center" data-testid="net-income-before-tax">
              <span className="text-lg font-semibold">Net Income Before Tax</span>
              <span className={`text-2xl font-bold font-mono ${profitLoss.net_income_before_tax >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(profitLoss.net_income_before_tax)}
              </span>
            </div>
            {profitLoss.profit_margin !== undefined && (
              <p className="text-sm text-slate-500 mt-2 text-right italic">
                Profit margin: {profitLoss.profit_margin}% of total revenue
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tax Summary */}
      {taxSummary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Receipt className="w-5 h-5" /> Tax Summary ({fiscalYear})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-blue-50 rounded-lg text-center"><p className="text-sm text-blue-600">Gross Income</p><p className="text-2xl font-bold text-blue-700">{formatCurrency(taxSummary.gross_income)}</p></div>
              <div className="p-4 bg-amber-50 rounded-lg text-center"><p className="text-sm text-amber-600">Total Deductions</p><p className="text-2xl font-bold text-amber-700">{formatCurrency(taxSummary.total_deductions)}</p></div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center"><p className="text-sm text-emerald-600">Taxable Income</p><p className="text-2xl font-bold text-emerald-700">{formatCurrency(taxSummary.taxable_income)}</p></div>
            </div>
            <h4 className="font-semibold mb-3">Tax-Deductible Expense Breakdown</h4>
            <div className="space-y-2">
              {taxSummary.expense_breakdown.map((item, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b">
                  <span className="flex items-center gap-2">{item.category}{item.tax_deductible && <Badge variant="outline" className="text-xs">Deductible</Badge>}</span>
                  <span className="font-mono">{formatCurrency(item.amount)}</span>
                </div>
              ))}
            </div>
            {taxSummary.shareholder_tax_summary && taxSummary.shareholder_tax_summary.by_shareholder?.length > 0 && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Shareholder Tax Slip Summary</h4>
                <Table>
                  <TableHeader><TableRow><TableHead>Shareholder</TableHead><TableHead className="text-right">Total Earnings</TableHead><TableHead className="text-right">T4A (Commission)</TableHead><TableHead className="text-right">T5 (Dividend)</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {taxSummary.shareholder_tax_summary.by_shareholder.map((sh, idx) => (
                      <TableRow key={idx}><TableCell className="font-medium">{sh.name}</TableCell><TableCell className="text-right font-mono">{formatCurrency(sh.total)}</TableCell><TableCell className="text-right font-mono text-amber-600">{formatCurrency(sh.commission_t4a)}</TableCell><TableCell className="text-right font-mono text-green-600 font-bold">{formatCurrency(sh.dividend_t5)}</TableCell></TableRow>
                    ))}
                    <TableRow className="bg-slate-50 font-bold"><TableCell>Total</TableCell><TableCell className="text-right font-mono">{formatCurrency(taxSummary.shareholder_tax_summary.total_shareholder_earnings)}</TableCell><TableCell className="text-right font-mono text-amber-600">{formatCurrency(taxSummary.shareholder_tax_summary.commissions_t4a)}</TableCell><TableCell className="text-right font-mono text-green-600">{formatCurrency(taxSummary.shareholder_tax_summary.dividends_t5)}</TableCell></TableRow>
                  </TableBody>
                </Table>
                <p className="text-xs text-green-700 mt-2">T5 dividends are reported in Box 24 (Eligible Dividends) with 38% gross-up for taxable amount.</p>
              </div>
            )}
            <div className="mt-4 p-4 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-800"><strong>Note:</strong> {taxSummary.notes?.[0] || 'Consult with a tax professional for accurate filing.'}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PDF Reports Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> Generate PDF Reports (Canadian Standard)</CardTitle>
          <CardDescription>Preview reports with account summaries before downloading. Click on any account to see source transactions.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Financial Statements</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {[
                  { type: 'income-statement', label: 'Income Statement\n(P&L) Preview', color: 'text-emerald-600' },
                  { type: 'balance-sheet', label: 'Balance Sheet\nPreview', color: 'text-blue-600' },
                  { type: 'cash-flow', label: 'Cash Flow\nPreview', color: 'text-purple-600' },
                  { type: 'general-ledger', label: 'General Ledger\nPreview', color: 'text-amber-600' },
                  { type: 'trial-balance', label: 'Trial Balance\nPreview', color: 'text-slate-600' },
                ].map(r => (
                  <Button key={r.type} variant="outline" className="h-auto py-3 flex flex-col items-center gap-2" onClick={() => loadReportPreview(r.type)} disabled={reportPreviewLoading} data-testid={`preview-${r.type}`}>
                    <Eye className={`w-5 h-5 ${r.color}`} />
                    <span className="text-xs text-center whitespace-pre-line">{r.label}</span>
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" /> Corporate Tax Reports</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { id: 't2-corporate-tax', file: `T2_Corporate_Tax_${fiscalYear}.pdf`, label: 'T2 Corporate\nIncome Tax', testid: 'download-t2' },
                  { id: 'financial-statements', file: `Financial_Statements_${fiscalYear}.pdf`, label: 'Year-End\nFinancials', testid: 'download-financial-statements' },
                  { id: 'schedule-8-cca', file: `Schedule_8_CCA_${fiscalYear}.pdf`, label: 'Schedule 8\n(CCA)', testid: 'download-schedule-8' },
                  { id: 't4-t4a-summary', file: `T4_T4A_Summary_${fiscalYear}.pdf`, label: 'T4/T4A\nSummary', testid: 'download-t4-t4a-summary' },
                ].map(r => (
                  <Button key={r.id} variant="outline" className="h-auto py-3 flex flex-col items-center gap-2 border-red-200 hover:bg-red-50" onClick={() => downloadPdfReport(r.id, r.file)} data-testid={r.testid}>
                    <Landmark className="w-5 h-5 text-red-600" />
                    <span className="text-xs text-center whitespace-pre-line">{r.label}</span>
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2"><Receipt className="w-4 h-4" /> Sales Tax Reports (GST/HST/PST)</h4>
              <div className="mb-3">
                <Label className="text-sm text-slate-600">Select Quarter</Label>
                <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
                  <SelectTrigger className="w-48 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                    <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                    <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                    <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-2 border-emerald-200 hover:bg-emerald-50" onClick={() => downloadPdfReport(`gst-hst-return?fiscal_year=${fiscalYear}&quarter=${selectedQuarter}`, `GST_HST_Return_Q${selectedQuarter}_${fiscalYear}.pdf`)} data-testid="download-gst-hst">
                  <DollarSign className="w-5 h-5 text-emerald-600" /><span className="text-xs text-center">GST/HST<br/>Return</span>
                </Button>
                {[
                  { prov: 'mb', label: 'MB', name: 'RST' }, { prov: 'bc', label: 'BC', name: 'PST' },
                  { prov: 'sk', label: 'SK', name: 'PST' }, { prov: 'on', label: 'ON', name: 'HST' },
                  { prov: 'qc', label: 'QC', name: 'QST' },
                ].map(p => (
                  <Button key={p.prov} variant="outline" className="h-auto py-3 flex flex-col items-center gap-2" onClick={() => downloadPdfReport(`pst-return/${p.prov}?fiscal_year=${fiscalYear}&quarter=${selectedQuarter}`, `${p.name}_${p.label}_Q${selectedQuarter}_${fiscalYear}.pdf`)} data-testid={`download-pst-${p.prov}`}>
                    <span className="text-xs font-bold text-slate-600">{p.label}</span><span className="text-xs text-center">{p.name}</span>
                  </Button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3">Note: Additional provinces (NS, NB, NL, PE) available. Alberta, Yukon, NWT, and Nunavut do not have PST.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Preview Modal */}
      <Dialog open={showReportPreview} onOpenChange={setShowReportPreview}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" /> Report Preview - {selectedReportType.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </DialogTitle>
            <DialogDescription>
              {reportPreviewData?.period && (
                <span>Period: {reportPreviewData.period.start} to {reportPreviewData.period.end} | {reportPreviewData.accounts_with_activity} accounts with activity | {reportPreviewData.total_transactions} transactions</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {reportPreviewData && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg text-center"><p className="text-sm text-emerald-600">Total Income</p><p className="text-xl font-bold text-emerald-700">{formatCurrency(reportPreviewData.totals.total_income)}</p></div>
                <div className="p-4 bg-red-50 rounded-lg text-center"><p className="text-sm text-red-600">Total Expenses</p><p className="text-xl font-bold text-red-700">{formatCurrency(reportPreviewData.totals.total_expenses)}</p></div>
                <div className="p-4 bg-blue-50 rounded-lg text-center"><p className="text-sm text-blue-600">Net Profit</p><p className={`text-xl font-bold ${reportPreviewData.totals.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{formatCurrency(reportPreviewData.totals.net_profit)}</p></div>
                <div className="p-4 bg-purple-50 rounded-lg text-center"><p className="text-sm text-purple-600">Total Assets</p><p className="text-xl font-bold text-purple-700">{formatCurrency(reportPreviewData.totals.total_assets)}</p></div>
              </div>
              {renderAccountSection(reportPreviewData.summaries.income, 'Income Accounts', TrendingUp, 'text-emerald-700', 'total_debit')}
              {renderAccountSection(reportPreviewData.summaries.expense, 'Expense Accounts', TrendingDown, 'text-red-700', 'total_credit')}
              {reportPreviewData.summaries.asset?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2"><Wallet className="w-4 h-4" /> Asset Accounts</h4>
                  <div className="space-y-2">
                    {reportPreviewData.summaries.asset.map(account => (
                      <div key={account.account_id} className="border rounded-lg">
                        <div className="p-3 flex justify-between items-center cursor-pointer hover:bg-blue-50 transition-colors" onClick={() => toggleAccountExpand(account.account_id)}>
                          <div className="flex items-center gap-3">
                            {expandedAccounts[account.account_id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            <span className="font-mono text-sm text-slate-500">{account.account_number}</span>
                            <span className="font-medium">{account.account_name}</span>
                            <Badge variant="outline" className="text-xs">{account.transaction_count} trans</Badge>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-mono font-bold text-blue-600">{formatCurrency(account.net_balance)}</span>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); loadAccountDrilldown(account); }}><ExternalLink className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {reportPreviewData.summaries.liability?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Liability Accounts</h4>
                  <div className="space-y-2">
                    {reportPreviewData.summaries.liability.map(account => (
                      <div key={account.account_id} className="border rounded-lg p-3 flex justify-between items-center cursor-pointer hover:bg-amber-50" onClick={() => loadAccountDrilldown(account)}>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-sm text-slate-500">{account.account_number}</span>
                          <span className="font-medium">{account.account_name}</span>
                          <Badge variant="outline" className="text-xs">{account.transaction_count} trans</Badge>
                        </div>
                        <span className="font-mono font-bold text-amber-600">{formatCurrency(account.net_balance)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex justify-between mt-4">
            <Button variant="outline" onClick={() => setShowReportPreview(false)}>Close</Button>
            <Button onClick={() => downloadPdfReport(selectedReportType, `${selectedReportType.replace('-', '_')}_${fiscalYear}.pdf`)}>
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account Drilldown Modal */}
      <Dialog open={showAccountDrilldown} onOpenChange={setShowAccountDrilldown}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> {drilldownAccount?.account_name} - Transactions</DialogTitle>
            <DialogDescription>Account #{drilldownAccount?.account_number} | {drilldownTransactions.length} transactions</DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Reference</TableHead>
                <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drilldownTransactions.map(trans => (
                <TableRow key={trans.id}>
                  <TableCell>{trans.date}</TableCell>
                  <TableCell className="max-w-[250px]">
                    <div className="truncate">{trans.description}</div>
                    {trans.invoice_number && <Badge variant="outline" className="text-xs mt-1">Inv: {trans.invoice_number}</Badge>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{trans.reference || '-'}</TableCell>
                  <TableCell className="text-right font-mono">{trans.debit > 0 ? formatCurrency(trans.debit) : '-'}</TableCell>
                  <TableCell className="text-right font-mono">{trans.credit > 0 ? formatCurrency(trans.credit) : '-'}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => navigateToTransactionById(trans.id)} title="Edit Transaction"><Edit2 className="w-4 h-4" /></Button>
                      {trans.invoice_id && <Button size="sm" variant="ghost" onClick={() => navigateToInvoiceById()} title="View Invoice"><FileText className="w-4 h-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DialogFooter><Button variant="outline" onClick={() => setShowAccountDrilldown(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comparative P&L */}
      <Card data-testid="comparative-pl-card">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Year-over-Year P&L Comparison</CardTitle>
              <CardDescription>Compare financial performance across two fiscal years</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={compYear1.toString()} onValueChange={(v) => setCompYear1(parseInt(v))}>
                <SelectTrigger className="w-[100px]" data-testid="comp-year1-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[2023, 2024, 2025, 2026].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-slate-400 font-medium">vs</span>
              <Select value={compYear2.toString()} onValueChange={(v) => setCompYear2(parseInt(v))}>
                <SelectTrigger className="w-[100px]" data-testid="comp-year2-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[2023, 2024, 2025, 2026].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {comparativeLoading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>
          ) : compYear1 === compYear2 ? (
            <p className="text-center py-8 text-slate-500">Select two different years to compare</p>
          ) : comparative ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold w-[40%]">Line Item</TableHead>
                    <TableHead className="text-right font-semibold">{comparative.year1}</TableHead>
                    <TableHead className="text-right font-semibold">{comparative.year2}</TableHead>
                    <TableHead className="text-right font-semibold">$ Change</TableHead>
                    <TableHead className="text-right font-semibold">% Change</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Revenue */}
                  <TableRow className="bg-emerald-50/50"><TableCell colSpan={5} className="font-bold text-emerald-700 text-sm">Revenue</TableCell></TableRow>
                  {comparative.income.map((item, i) => (
                    <CompRow key={i} label={item.category} v1={item.year1} v2={item.year2} change={item.change} indent />
                  ))}
                  <CompTotalRow label="Total Revenue" data={comparative.total_income} className="text-emerald-700 bg-emerald-50" />

                  {/* Cost of Sales */}
                  <TableRow className="bg-orange-50/50"><TableCell colSpan={5} className="font-bold text-orange-700 text-sm">Cost of Sales</TableCell></TableRow>
                  {comparative.cost_of_sales.map((item, i) => (
                    <CompRow key={i} label={item.category} v1={item.year1} v2={item.year2} change={item.change} indent invertColor />
                  ))}
                  <CompTotalRow label="Total Cost of Sales" data={comparative.total_cost_of_sales} className="text-orange-700 bg-orange-50" invertColor />

                  {/* Gross Profit */}
                  <CompTotalRow label="Gross Profit" data={comparative.gross_profit} className="text-blue-800 bg-blue-50 font-bold" />

                  {/* Operating Expenses */}
                  <TableRow className="bg-red-50/50"><TableCell colSpan={5} className="font-bold text-red-700 text-sm">Operating Expenses</TableCell></TableRow>
                  {comparative.operating_expenses.map((item, i) => (
                    <CompRow key={i} label={item.category} v1={item.year1} v2={item.year2} change={item.change} indent invertColor />
                  ))}
                  <CompTotalRow label="Total Operating Expenses" data={comparative.total_operating_expenses} className="text-red-700 bg-red-50" invertColor />

                  {/* Net Income Before Tax */}
                  <CompTotalRow label="Net Income Before Tax" data={comparative.net_income_before_tax} className="text-slate-900 bg-slate-100 font-bold text-base" />
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center py-8 text-slate-500">Loading comparative data...</p>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function ChangeIndicator({ change, invert = false }) {
  if (!change) return null;
  const isPositive = invert ? change.dollar < 0 : change.dollar > 0;
  const isNegative = invert ? change.dollar > 0 : change.dollar < 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${isPositive ? 'text-emerald-600' : isNegative ? 'text-red-600' : 'text-slate-400'}`}>
      {isPositive ? <ArrowUpRight className="w-3 h-3" /> : isNegative ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
      {Math.abs(change.percent)}%
    </span>
  );
}

function CompRow({ label, v1, v2, change, indent = false, invertColor = false }) {
  return (
    <TableRow className="text-sm">
      <TableCell className={indent ? 'pl-8' : ''}>{label}</TableCell>
      <TableCell className="text-right font-mono">{formatCurrency(v1)}</TableCell>
      <TableCell className="text-right font-mono">{formatCurrency(v2)}</TableCell>
      <TableCell className={`text-right font-mono ${change.dollar > 0 ? (invertColor ? 'text-red-600' : 'text-emerald-600') : change.dollar < 0 ? (invertColor ? 'text-emerald-600' : 'text-red-600') : 'text-slate-400'}`}>
        {change.dollar >= 0 ? '+' : ''}{formatCurrency(change.dollar)}
      </TableCell>
      <TableCell className="text-right"><ChangeIndicator change={change} invert={invertColor} /></TableCell>
    </TableRow>
  );
}

function CompTotalRow({ label, data, className = '', invertColor = false }) {
  return (
    <TableRow className={`font-semibold ${className}`}>
      <TableCell>{label}</TableCell>
      <TableCell className="text-right font-mono">{formatCurrency(data.year1)}</TableCell>
      <TableCell className="text-right font-mono">{formatCurrency(data.year2)}</TableCell>
      <TableCell className={`text-right font-mono ${data.change.dollar > 0 ? (invertColor ? 'text-red-600' : 'text-emerald-600') : data.change.dollar < 0 ? (invertColor ? 'text-emerald-600' : 'text-red-600') : 'text-slate-400'}`}>
        {data.change.dollar >= 0 ? '+' : ''}{formatCurrency(data.change.dollar)}
      </TableCell>
      <TableCell className="text-right"><ChangeIndicator change={data.change} invert={invertColor} /></TableCell>
    </TableRow>
  );
}
